import { Vector3 } from 'three';
import { type BrushFace, srcDirToWorld } from './faces';
import {
  type DispVert,
  readDispInfo,
  readDispVerts,
  readEdges,
  readFaces,
  readPlanes,
  readSurfedges,
  readVertexes,
} from './lumps';
import type { BspReader } from './reader';
import { srcToWorld } from './transform';

/**
 * Displacement (terrain) collision. Source builds smooth terrain by subdividing a
 * 4-corner base face into a (2^power + 1)² grid and pushing each grid vertex along
 * a per-vertex offset (DISP_VERTS). Brush geometry alone misses all of it — maps
 * built on displacements (surf_garden, surf_me, surf_cyberwave, and many scenery
 * surf maps) import with no ride surface. This rebuilds the grid into triangles so
 * those maps are surfable. Reference: developer.valvesoftware.com/wiki/Displacement.
 */

/** Source-space vertices of a face, walked SURFEDGES → EDGES → VERTEXES. */
function faceCorners(
  firstedge: number,
  numedges: number,
  surfedges: number[],
  edges: [number, number][],
  verts: [number, number, number][],
): Vector3[] {
  const out: Vector3[] = [];
  for (let i = 0; i < numedges; i++) {
    const se = surfedges[firstedge + i];
    if (se === undefined) continue;
    const edge = edges[Math.abs(se)];
    if (!edge) continue;
    const v = verts[se >= 0 ? edge[0] : edge[1]];
    if (v) out.push(new Vector3(v[0], v[1], v[2]));
  }
  return out;
}

export function buildDisplacementFaces(reader: BspReader): BrushFace[] {
  const dispInfos = readDispInfo(reader);
  if (dispInfos.length === 0) return [];
  const faces = readFaces(reader);
  const planes = readPlanes(reader);
  const verts = readVertexes(reader);
  const edges = readEdges(reader);
  const surfedges = readSurfedges(reader);
  const dispVerts = readDispVerts(reader);

  const out: BrushFace[] = [];
  const start = new Vector3();

  for (const face of faces) {
    if (face.dispinfo < 0) continue;
    const di = dispInfos[face.dispinfo];
    if (!di) continue;
    const corners = faceCorners(face.firstedge, face.numedges, surfedges, edges, verts);
    if (corners.length !== 4) continue; // displacements are always quads

    // Reorder so the corner nearest startPosition is c0 (the grid origin).
    start.set(di.startPosition[0], di.startPosition[1], di.startPosition[2]);
    let k = 0;
    let best = Infinity;
    for (let c = 0; c < 4; c++) {
      const d = (corners[c] as Vector3).distanceToSquared(start);
      if (d < best) {
        best = d;
        k = c;
      }
    }
    const c0 = corners[k] as Vector3;
    const c1 = corners[(k + 1) % 4] as Vector3;
    const c2 = corners[(k + 2) % 4] as Vector3;
    const c3 = corners[(k + 3) % 4] as Vector3;

    const side = (1 << di.power) + 1;
    const normal = srcDirToWorld(
      planes[face.planenum]?.nx ?? 0,
      planes[face.planenum]?.ny ?? 0,
      planes[face.planenum]?.nz ?? 1,
    );

    // Build the displaced grid in world space.
    const grid: Vector3[] = new Array(side * side);
    for (let i = 0; i < side; i++) {
      const fi = i / (side - 1);
      const rowStart = c0.clone().lerp(c1, fi);
      const rowEnd = c3.clone().lerp(c2, fi);
      for (let j = 0; j < side; j++) {
        const fj = j / (side - 1);
        const base = rowStart.clone().lerp(rowEnd, fj);
        const dv: DispVert | undefined = dispVerts[di.dispVertStart + i * side + j];
        if (dv) {
          base.x += dv.vec[0] * dv.dist;
          base.y += dv.vec[1] * dv.dist;
          base.z += dv.vec[2] * dv.dist;
        }
        grid[i * side + j] = srcToWorld(base.x, base.y, base.z);
      }
    }

    // Two triangles per grid cell.
    for (let i = 0; i < side - 1; i++) {
      for (let j = 0; j < side - 1; j++) {
        const a = grid[i * side + j] as Vector3;
        const b = grid[(i + 1) * side + j] as Vector3;
        const c = grid[(i + 1) * side + j + 1] as Vector3;
        const d = grid[i * side + j + 1] as Vector3;
        out.push({ verts: [a, b, c], normal }, { verts: [a, c, d], normal });
      }
    }
  }
  return out;
}
